import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { PLYLoader } from 'three/addons/loaders/PLYLoader.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { supabase } from '../supabaseClient';
import {
    FaTimes, FaUndo, FaSpinner, FaCube, FaCamera,
    FaEye, FaEyeSlash, FaMinusSquare, FaPlusSquare,
    FaSlidersH, FaExpand, FaCompress
} from 'react-icons/fa';

// Couleurs de rendu : Haut et Bas en gris clair dentaire identique, Occlusion foncée contrastée
const CATEGORY_STYLES = {
    maxillaire: { label: 'Maxillaire', color: 0xe6e9ee, cssColor: '#e6e9ee', defaultExpanded: true },
    mandibule: { label: 'Mandibule', color: 0xe6e9ee, cssColor: '#e6e9ee', defaultExpanded: true },
    occlusion: { label: 'Occlusion', color: 0x334155, cssColor: '#334155', defaultExpanded: true },
    autres: { label: 'Autres fichiers', color: 0x94d2bd, cssColor: '#94d2bd', defaultExpanded: true }
};

export const isViewable = (name = '') => /\.(stl|ply|obj)$/i.test(name || '');
export const canViewFile = (file) => isViewable(file?.name);

// Détecte la catégorie dentaire (utilise la catégorie choisie lors du dépôt ou fallback nom)
const categorizeFile = (file = {}) => {
    if (typeof file === 'object' && file?.category) return file.category;
    const fileName = typeof file === 'string' ? file : (file?.name || '');
    const lower = fileName.toLowerCase();
    if (lower.includes('maxill') || lower.includes('upper') || lower.includes('haut')) {
        return 'maxillaire';
    }
    if (lower.includes('mandib') || lower.includes('lower') || lower.includes('bas')) {
        return 'mandibule';
    }
    if (lower.includes('occlus') || lower.includes('bite') || lower.includes('articul')) {
        return 'occlusion';
    }
    return 'autres';
};

// Nom d'affichage simplifié dans l'arborescence (ex: "Base", "PreOP", etc.)
const getCleanSubName = (fileName = '', cat = '') => {
    let name = fileName.replace(/\.(stl|ply|obj)$/i, '');
    const lower = name.toLowerCase();
    if (lower.includes('preop') || lower.includes('pre_op') || lower.includes('pre-op')) return 'Pre-OP';
    if (lower.includes('base')) return 'Base';
    if (lower.includes('scanbody') || lower.includes('scan_body')) return 'Scanbody';
    if (lower.includes('prep') || lower.includes('preparation')) return 'Préparation';
    if (lower.includes('first') || lower.includes('1') || lower.includes('premiere')) return 'Première occlusion';
    if (lower.includes('second') || lower.includes('2') || lower.includes('deuxieme')) return 'Deuxième occlusion';
    return name;
};

function STLViewerModal({ files = [], initialFile, onClose }) {
    const viewableFiles = files.filter(canViewFile);

    // Identifiant stable par fichier
    const getFileId = (f) => f?.path || f?.name || f?.url;

    // Regrouper par catégorie dentaire
    const groupedFiles = React.useMemo(() => {
        const groups = { maxillaire: [], mandibule: [], occlusion: [], autres: [] };
        viewableFiles.forEach((f) => {
            const cat = categorizeFile(f);
            groups[cat].push(f);
        });
        return groups;
    }, [viewableFiles]);

    // Visibilité par fichier (par défaut : TOUS les fichiers dentaires sont visibles pour voir l'engrènement)
    const [visibility, setVisibility] = useState(() => {
        const initial = {};
        viewableFiles.forEach((f) => {
            initial[getFileId(f)] = true;
        });
        return initial;
    });

    // Opacité par fichier (1 = 100%, 0.5 = 50%, etc.)
    const [opacity, setOpacity] = useState(() => {
        const initial = {};
        viewableFiles.forEach((f) => {
            initial[getFileId(f)] = 1.0;
        });
        return initial;
    });

    // Expansion des dossiers dans l'arbre
    const [expandedCategories, setExpandedCategories] = useState({
        maxillaire: true,
        mandibule: true,
        occlusion: true,
        autres: true
    });

    const [fileStatus, setFileStatus] = useState({});
    const [wireframe, setWireframe] = useState(false);
    const [activeTab, setActiveTab] = useState('tree'); // 'tree' ou 'views'
    const [isFullscreen, setIsFullscreen] = useState(false);

    const mountRef = useRef(null);
    const modalContainerRef = useRef(null);
    const sceneRef = useRef(null);
    const originRef = useRef(null); // Origine partagée pour caler occlusion maxillaire / mandibule

    // Initialisation Scène Three.js Medit-style
    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        // 1. Scène avec fond clair type Medit Web Viewer
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf1f4f9);

        // 2. Caméra perspective
        const width = mount.clientWidth || 800;
        const height = mount.clientHeight || 600;
        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 10000);
        camera.position.set(0, 0, 160);

        // 3. Renderer avec ombrage doux
        const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: false });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(width, height);
        mount.appendChild(renderer.domElement);

        // 4. Éclairage Medit Link : Lumières douces multidirectionnelles + Headlight
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.85);
        scene.add(ambientLight);

        // Lumière principale supérieure avant
        const dirLightFront = new THREE.DirectionalLight(0xffffff, 0.95);
        dirLightFront.position.set(1, 2, 2).normalize();
        scene.add(dirLightFront);

        // Lumière de remplissage arrière
        const dirLightBack = new THREE.DirectionalLight(0xdde5ed, 0.6);
        dirLightBack.position.set(-1, -1, -1).normalize();
        scene.add(dirLightBack);

        // Headlight accrochée à la caméra (illumine toujours ce que regarde le praticien)
        const headLight = new THREE.DirectionalLight(0xffffff, 0.5);
        headLight.position.set(0, 0, 1);
        camera.add(headLight);
        scene.add(camera);

        // 5. Contrôles Orbit
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.rotateSpeed = 0.9;
        controls.zoomSpeed = 1.2;
        controls.panSpeed = 0.8;

        const group = new THREE.Group();
        scene.add(group);

        let animationFrameId;
        const animate = () => {
            animationFrameId = requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();

        // Observer pour redimensionnement automatique continu
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { width: newW, height: newH } = entry.contentRect;
                if (newW > 10 && newH > 10) {
                    camera.aspect = newW / newH;
                    camera.updateProjectionMatrix();
                    renderer.setSize(newW, newH);
                }
            }
        });
        resizeObserver.observe(mount);

        sceneRef.current = {
            scene,
            camera,
            renderer,
            controls,
            group,
            meshes: {} // fileId -> THREE.Mesh
        };

        return () => {
            cancelAnimationFrame(animationFrameId);
            resizeObserver.disconnect();
            controls.dispose();

            group.traverse((obj) => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) {
                    if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
                    else obj.material.dispose();
                }
            });

            renderer.dispose();
            if (renderer.domElement && renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
            sceneRef.current = null;
            originRef.current = null;
        };
    }, []);

    // Cadrage automatique sur l'ensemble des maillages visibles
    const fitCamera = useCallback(() => {
        const ctx = sceneRef.current;
        if (!ctx || !ctx.group) return;

        const { camera, controls, group } = ctx;
        const box = new THREE.Box3();
        let hasVisibleMesh = false;

        group.children.forEach((child) => {
            if (child.visible) {
                box.expandByObject(child);
                hasVisibleMesh = true;
            }
        });

        if (!hasVisibleMesh || box.isEmpty()) return;

        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) || 60;

        const fov = camera.fov * (Math.PI / 180);
        const cameraDistance = (maxDim / 2) / Math.tan(fov / 2) * 1.5;

        controls.target.copy(center);
        camera.position.set(center.x, center.y, center.z + cameraDistance);
        camera.near = Math.max(maxDim / 500, 0.1);
        camera.far = Math.max(maxDim * 500, 8000);
        camera.updateProjectionMatrix();
        controls.update();
    }, []);

    // Vues standard dentaires
    const setViewPreset = (viewType) => {
        const ctx = sceneRef.current;
        if (!ctx) return;
        const { camera, controls, group } = ctx;

        const box = new THREE.Box3().setFromObject(group);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) || 60;
        const dist = (maxDim / 2) / Math.tan((camera.fov * Math.PI) / 360) * 1.5;

        controls.target.copy(center);

        if (viewType === 'front') {
            // Vue de face (vestibulaire)
            camera.position.set(center.x, center.y, center.z + dist);
            camera.up.set(0, 1, 0);
        } else if (viewType === 'top') {
            // Vue occlusale (du dessus)
            camera.position.set(center.x, center.y + dist, center.z);
            camera.up.set(0, 0, -1);
        } else if (viewType === 'bottom') {
            // Vue du dessous
            camera.position.set(center.x, center.y - dist, center.z);
            camera.up.set(0, 0, 1);
        } else if (viewType === 'right') {
            // Vue latérale droite
            camera.position.set(center.x + dist, center.y, center.z);
            camera.up.set(0, 1, 0);
        } else if (viewType === 'left') {
            // Vue latérale gauche
            camera.position.set(center.x - dist, center.y, center.z);
            camera.up.set(0, 1, 0);
        }

        camera.updateProjectionMatrix();
        controls.update();
    };

    // Chargement & parsing du fichier 3D
    const loadFileGeometry = async (file) => {
        const fileId = getFileId(file);
        const ext = (file.name || '').split('.').pop().toLowerCase();
        const cat = categorizeFile(file);
        const catConfig = CATEGORY_STYLES[cat] || CATEGORY_STYLES.autres;

        setFileStatus((prev) => ({
            ...prev,
            [fileId]: { state: 'loading', message: 'Téléchargement...' }
        }));

        try {
            let arrayBuffer = null;

            // 1. Essai de téléchargement direct depuis le bucket Supabase
            if (file.path) {
                try {
                    const { data: blob, error } = await supabase.storage
                        .from('stl-files')
                        .download(file.path);

                    if (!error && blob) {
                        arrayBuffer = await blob.arrayBuffer();
                    }
                } catch (storageErr) {
                    console.warn('Storage direct failed:', storageErr);
                }
            }

            // 2. Si non réussi, essai via l'URL
            if (!arrayBuffer) {
                const targetUrl = file.url || (file.path ? supabase.storage.from('stl-files').getPublicUrl(file.path).data?.publicUrl : null);
                if (!targetUrl) throw new Error("URL introuvable");

                const response = await fetch(targetUrl);
                if (!response.ok) throw new Error(`Erreur réseau (${response.status})`);
                arrayBuffer = await response.arrayBuffer();
            }

            if (!arrayBuffer || arrayBuffer.byteLength === 0) {
                throw new Error("Fichier vide");
            }

            setFileStatus((prev) => ({
                ...prev,
                [fileId]: { state: 'loading', message: 'Rendu 3D...' }
            }));

            // Matériau réaliste Medit avec transparence possible
            const fileOpacity = opacity[fileId] !== undefined ? opacity[fileId] : 1.0;
            const material = new THREE.MeshStandardMaterial({
                color: catConfig.color,
                roughness: 0.35,
                metalness: 0.05,
                side: THREE.DoubleSide,
                wireframe: wireframe,
                transparent: fileOpacity < 1.0,
                opacity: fileOpacity
            });

            let meshObject = null;

            if (ext === 'stl') {
                const loader = new STLLoader();
                const geometry = loader.parse(arrayBuffer);
                geometry.computeVertexNormals();

                // Centrage : le premier fichier chargé fixe l'origine partagée
                geometry.computeBoundingBox();
                const fileCenter = geometry.boundingBox.getCenter(new THREE.Vector3());

                if (!originRef.current) {
                    originRef.current = fileCenter.clone();
                }

                geometry.translate(-originRef.current.x, -originRef.current.y, -originRef.current.z);
                meshObject = new THREE.Mesh(geometry, material);
            } else if (ext === 'ply') {
                const loader = new PLYLoader();
                const geometry = loader.parse(arrayBuffer);
                geometry.computeVertexNormals();

                if (geometry.getAttribute('color')) {
                    material.vertexColors = true;
                    material.color.setHex(0xffffff);
                }

                geometry.computeBoundingBox();
                const fileCenter = geometry.boundingBox.getCenter(new THREE.Vector3());
                if (!originRef.current) {
                    originRef.current = fileCenter.clone();
                }
                geometry.translate(-originRef.current.x, -originRef.current.y, -originRef.current.z);
                meshObject = new THREE.Mesh(geometry, material);
            } else if (ext === 'obj') {
                const loader = new OBJLoader();
                const text = new TextDecoder('utf-8').decode(arrayBuffer);
                meshObject = loader.parse(text);

                meshObject.traverse((child) => {
                    if (child.isMesh) {
                        child.material = material;
                        child.geometry.computeVertexNormals();
                    }
                });

                const box = new THREE.Box3().setFromObject(meshObject);
                const fileCenter = box.getCenter(new THREE.Vector3());
                if (!originRef.current) {
                    originRef.current = fileCenter.clone();
                }
                meshObject.position.sub(originRef.current);
            }

            if (meshObject && sceneRef.current) {
                sceneRef.current.meshes[fileId] = meshObject;
                sceneRef.current.group.add(meshObject);
                meshObject.visible = visibility[fileId] !== false;

                setFileStatus((prev) => ({
                    ...prev,
                    [fileId]: { state: 'ok', message: 'Prêt' }
                }));

                fitCamera();
            }
        } catch (err) {
            console.error(`Erreur STL [${file.name}]:`, err);
            setFileStatus((prev) => ({
                ...prev,
                [fileId]: { state: 'error', message: err.message || 'Erreur chargement' }
            }));
        }
    };

    // Chargement réactif des fichiers au montage / changement de visibilité
    useEffect(() => {
        if (!sceneRef.current) return;

        viewableFiles.forEach((file) => {
            const fileId = getFileId(file);
            const isVisible = visibility[fileId] !== false;
            const existingMesh = sceneRef.current.meshes[fileId];

            if (isVisible) {
                if (existingMesh) {
                    existingMesh.visible = true;
                } else if (!fileStatus[fileId] || fileStatus[fileId].state === 'error') {
                    loadFileGeometry(file);
                }
            } else if (existingMesh) {
                existingMesh.visible = false;
            }
        });

        fitCamera();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibility]);

    // Mise à jour de l'opacité
    useEffect(() => {
        const ctx = sceneRef.current;
        if (!ctx) return;
        Object.entries(opacity).forEach(([fileId, val]) => {
            const mesh = ctx.meshes[fileId];
            if (mesh) {
                mesh.traverse((child) => {
                    if (child.material) {
                        child.material.transparent = val < 1.0;
                        child.material.opacity = val;
                        child.material.needsUpdate = true;
                    }
                });
            }
        });
    }, [opacity]);

    // Mode fil de fer (wireframe)
    useEffect(() => {
        const ctx = sceneRef.current;
        if (!ctx) return;
        Object.values(ctx.meshes).forEach((mesh) => {
            if (mesh) {
                mesh.traverse((child) => {
                    if (child.material) child.material.wireframe = wireframe;
                });
            }
        });
    }, [wireframe]);

    // Toggle visibilité d'un fichier
    const toggleFileVisibility = (fileId, e) => {
        if (e) e.stopPropagation();
        setVisibility((prev) => ({
            ...prev,
            [fileId]: !prev[fileId]
        }));
    };

    // Toggle visibilité de tout un groupe (ex: tout le Maxillaire)
    const toggleCategoryVisibility = (catKey, e) => {
        if (e) e.stopPropagation();
        const filesInCat = groupedFiles[catKey] || [];
        const anyVisible = filesInCat.some((f) => visibility[getFileId(f)] !== false);

        const updated = { ...visibility };
        filesInCat.forEach((f) => {
            updated[getFileId(f)] = !anyVisible;
        });
        setVisibility(updated);
    };

    // Toggle déplier / replier un dossier
    const toggleCategoryExpand = (catKey) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [catKey]: !prev[catKey]
        }));
    };

    // Raccourci Échap pour fermer
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Capture d'écran HD
    const handleScreenshot = () => {
        const ctx = sceneRef.current;
        if (!ctx || !ctx.renderer) return;

        ctx.renderer.render(ctx.scene, ctx.camera);
        const dataUrl = ctx.renderer.domElement.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `vue_3d_medit_${(initialFile?.name || 'cas').replace(/\.[^.]+$/, '')}.png`;
        link.href = dataUrl;
        link.click();
    };

    const toggleFullscreen = () => {
        if (!modalContainerRef.current) return;
        if (!document.fullscreenElement) {
            modalContainerRef.current.requestFullscreen().catch(() => {});
            setIsFullscreen(true);
        } else {
            document.exitFullscreen().catch(() => {});
            setIsFullscreen(false);
        }
    };

    const hasLoading = Object.values(fileStatus).some((s) => s.state === 'loading');

    return (
        <div
            ref={modalContainerRef}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                backgroundColor: 'rgba(15, 23, 42, 0.75)',
                backdropFilter: 'blur(6px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isFullscreen ? '0' : '16px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: '#ffffff',
                    width: isFullscreen ? '100vw' : 'min(1350px, 98vw)',
                    height: isFullscreen ? '100vh' : 'min(820px, 94vh)',
                    borderRadius: isFullscreen ? '0' : '14px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.4)',
                    border: '1px solid #cbd5e1'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 1. TOP HEADER (Style Medit Web Viewer) */}
                <div
                    style={{
                        height: '48px',
                        backgroundColor: '#ffffff',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 16px',
                        userSelect: 'none'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                            Visualiseur de fichiers
                        </span>
                        <span
                            style={{
                                fontSize: '11px',
                                backgroundColor: '#f1f5f9',
                                color: '#475569',
                                border: '1px solid #e2e8f0',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontWeight: '500'
                            }}
                        >
                            Style Medit
                        </span>
                    </div>

                    {/* Actions de droite */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                            type="button"
                            className={`btn btn-sm ${wireframe ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setWireframe(!wireframe)}
                            title="Activer/Désactiver le maillage filaire"
                            style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px' }}
                        >
                            Maillage
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={fitCamera}
                            title="Recentrer le modèle au centre de l'écran"
                            style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px' }}
                        >
                            <FaUndo size={11} className="me-1" /> Recentrer
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleScreenshot}
                            title="Prendre une capture d'écran HD"
                            style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}
                        >
                            <FaCamera size={13} />
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={toggleFullscreen}
                            title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                            style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}
                        >
                            {isFullscreen ? <FaCompress size={13} /> : <FaExpand size={13} />}
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={onClose}
                            title="Fermer (Échap)"
                            style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px' }}
                        >
                            <FaTimes size={13} />
                        </button>
                    </div>
                </div>

                {/* 2. CORPS : Sidebar Medit (gauche) + Zone 3D (droite) */}
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    {/* SIDEBAR GAUCHE (Exactement comme Medit) */}
                    <div
                        style={{
                            width: '270px',
                            backgroundColor: '#eef2f6',
                            borderRight: '1px solid #dce2ea',
                            display: 'flex',
                            flexDirection: 'column',
                            userSelect: 'none'
                        }}
                    >
                        {/* Onglets d'icônes en haut de la sidebar */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 14px',
                                borderBottom: '1px solid #dce2ea',
                                color: '#64748b',
                                fontSize: '13px'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', color: '#334155' }}>
                                <span>Arborescence</span>
                            </div>
                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                                {viewableFiles.length} fichier{viewableFiles.length > 1 ? 's' : ''}
                            </span>
                        </div>

                        {/* Liste arborescente des calques (Maxillaire, Mandibule, Occlusion) */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                            {Object.entries(groupedFiles).map(([catKey, catFiles]) => {
                                if (catFiles.length === 0) return null;
                                const catConfig = CATEGORY_STYLES[catKey] || CATEGORY_STYLES.autres;
                                const isExpanded = !!expandedCategories[catKey];
                                const allVisible = catFiles.every((f) => visibility[getFileId(f)] !== false);
                                const someVisible = catFiles.some((f) => visibility[getFileId(f)] !== false);

                                return (
                                    <div key={catKey} style={{ marginBottom: '4px' }}>
                                        {/* Ligne Catégorie (ex: Maxillaire) */}
                                        <div
                                            onClick={() => toggleCategoryExpand(catKey)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '7px 12px',
                                                backgroundColor: '#ffffff',
                                                borderTop: '1px solid #e2e8f0',
                                                borderBottom: '1px solid #e2e8f0',
                                                cursor: 'pointer',
                                                fontWeight: '600',
                                                fontSize: '13px',
                                                color: '#1e293b'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ color: '#64748b', fontSize: '12px' }}>
                                                    {isExpanded ? <FaMinusSquare /> : <FaPlusSquare />}
                                                </span>
                                                <span
                                                    style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        borderRadius: '3px',
                                                        backgroundColor: catConfig.cssColor,
                                                        border: '1px solid #cbd5e1'
                                                    }}
                                                />
                                                <span>{catConfig.label}</span>
                                            </div>

                                            {/* Oeil de groupe */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    onClick={(e) => toggleCategoryVisibility(catKey, e)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        padding: '2px',
                                                        cursor: 'pointer',
                                                        color: allVisible ? '#2563eb' : someVisible ? '#60a5fa' : '#94a3b8'
                                                    }}
                                                    title={allVisible ? "Masquer ce groupe" : "Afficher ce groupe"}
                                                >
                                                    {someVisible ? <FaEye size={15} /> : <FaEyeSlash size={15} />}
                                                </button>
                                                <span style={{ color: '#94a3b8', fontSize: '12px' }}>⋮</span>
                                            </div>
                                        </div>

                                        {/* Fichiers sous la catégorie */}
                                        {isExpanded && (
                                            <div style={{ backgroundColor: '#f8fafc' }}>
                                                {catFiles.map((file) => {
                                                    const fileId = getFileId(file);
                                                    const isFileVisible = visibility[fileId] !== false;
                                                    const cleanName = getCleanSubName(file.name, catKey);
                                                    const status = fileStatus[fileId];
                                                    const currentOpacity = opacity[fileId] !== undefined ? opacity[fileId] : 1.0;

                                                    return (
                                                        <div
                                                            key={fileId}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                padding: '6px 12px 6px 36px',
                                                                borderBottom: '1px solid #f1f5f9',
                                                                fontSize: '13px',
                                                                color: isFileVisible ? '#334155' : '#94a3b8'
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    maxWidth: '130px'
                                                                }}
                                                                title={file.name}
                                                            >
                                                                {cleanName}
                                                            </div>

                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                {status?.state === 'loading' ? (
                                                                    <FaSpinner className="spin-icon text-primary" size={12} />
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => toggleFileVisibility(fileId, e)}
                                                                        style={{
                                                                            background: 'none',
                                                                            border: 'none',
                                                                            padding: '2px',
                                                                            cursor: 'pointer',
                                                                            color: isFileVisible ? '#2563eb' : '#94a3b8'
                                                                        }}
                                                                        title={isFileVisible ? "Masquer ce scan" : "Afficher ce scan"}
                                                                    >
                                                                        {isFileVisible ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                                                                    </button>
                                                                )}

                                                                {/* Curseur de transparence / opacité */}
                                                                <input
                                                                    type="range"
                                                                    min="0.1"
                                                                    max="1"
                                                                    step="0.1"
                                                                    value={currentOpacity}
                                                                    onChange={(e) => {
                                                                        const val = parseFloat(e.target.value);
                                                                        setOpacity((prev) => ({ ...prev, [fileId]: val }));
                                                                    }}
                                                                    title={`Transparence : ${Math.round(currentOpacity * 100)}%`}
                                                                    style={{
                                                                        width: '38px',
                                                                        height: '4px',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Raccourcis de vues en bas de la sidebar (Face, Occlusale, etc.) */}
                        <div
                            style={{
                                padding: '12px 14px',
                                borderTop: '1px solid #dce2ea',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>
                                Vues Dentaires
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light border text-dark"
                                    onClick={() => setViewPreset('front')}
                                    style={{ fontSize: '11px', padding: '4px 6px' }}
                                >
                                    🦷 Face
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light border text-dark"
                                    onClick={() => setViewPreset('top')}
                                    style={{ fontSize: '11px', padding: '4px 6px' }}
                                >
                                    Occlusale
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light border text-dark"
                                    onClick={() => setViewPreset('left')}
                                    style={{ fontSize: '11px', padding: '4px 6px' }}
                                >
                                    Profil G
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light border text-dark"
                                    onClick={() => setViewPreset('right')}
                                    style={{ fontSize: '11px', padding: '4px 6px' }}
                                >
                                    Profil D
                                </button>
                            </div>
                        </div>

                        {/* Notice souris en pied de sidebar */}
                        <div
                            style={{
                                padding: '8px 14px',
                                borderTop: '1px solid #e2e8f0',
                                backgroundColor: '#f8fafc',
                                fontSize: '10px',
                                color: '#64748b',
                                lineHeight: '1.4'
                            }}
                        >
                            🖱️ <strong>Clic gauche :</strong> Tourner | <strong>Droit :</strong> Glisser | <strong>Molette :</strong> Zoom
                        </div>
                    </div>

                    {/* ZONE 3D THREE.JS (Fond clair Medit) */}
                    <div style={{ flex: 1, position: 'relative', minWidth: 0, backgroundColor: '#f1f4f9' }}>
                        <div ref={mountRef} style={{ width: '100%', height: '100%' }} />

                        {/* Indicateur de chargement en cours */}
                        {hasLoading && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '16px',
                                    left: '16px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.92)',
                                    backdropFilter: 'blur(4px)',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    fontSize: '13px',
                                    color: '#1e293b',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                                }}
                            >
                                <FaSpinner className="spin-icon text-primary" size={16} />
                                <span>Chargement des scans dentaires...</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default STLViewerModal;
